import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Plus,
  Trash2,
  Save,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import devRulesService from "../../api/devRulesService";
import { useDevRulesLock } from "../../context/DevRulesLockProvider";

const createEmptyPoint = () => ({ text: "", children: [] });
const createEmptyRule = () => ({ title: "", points: [createEmptyPoint()] });

const normalizePoint = (point) => {
  if (typeof point === "string") {
    return { text: point, children: [] };
  }

  if (!point || typeof point !== "object") {
    return createEmptyPoint();
  }

  return {
    text: String(point.text || ""),
    children: Array.isArray(point.children)
      ? point.children.map(normalizePoint)
      : [],
  };
};

const normalizeRule = (rule) => ({
  title: String(rule?.title || ""),
  points: Array.isArray(rule?.points)
    ? rule.points.map(normalizePoint)
    : [createEmptyPoint()],
});

const sanitizePoint = (point) => {
  const text = String(point?.text || "").trim();
  const children = Array.isArray(point?.children)
    ? point.children.map(sanitizePoint).filter(Boolean)
    : [];

  if (!text && children.length === 0) return null;
  return { text, children };
};

const hasAnyPointText = (point) => {
  if (!point) return false;
  if (point.text) return true;
  return Array.isArray(point.children) && point.children.some(hasAnyPointText);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DevRules = () => {
  const { auth } = useAuth();
  const employee = auth?.employee;
  const canManage = isAdmin(employee);
  const { lockState, acknowledgeLatestAndRefresh, refreshLockState } =
    useDevRulesLock();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({
    version: 0,
    updatedAt: null,
    updatedBy: null,
  });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const locked = Boolean(lockState?.locked);

  const fetchRules = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await devRulesService.getRules();
      const data = response?.data || {};
      setRules(Array.isArray(data.rules) ? data.rules.map(normalizeRule) : []);
      setMeta({
        version: data.version || 0,
        updatedAt: data.updatedAt || null,
        updatedBy: data.updatedBy || null,
      });
    } catch (err) {
      setError("Failed to load Dev Team Rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  useEffect(() => {
    const markSeen = async () => {
      if (!locked) return;
      try {
        await acknowledgeLatestAndRefresh();
      } catch (err) {
        // no-op; user can still read current rules even if ack call fails
      }
    };

    markSeen();
  }, [locked, acknowledgeLatestAndRefresh]);

  const addRule = () => {
    setRules((prev) => [...prev, createEmptyRule()]);
  };

  const updateRuleTitle = (index, value) => {
    setRules((prev) =>
      prev.map((rule, i) => (i === index ? { ...rule, title: value } : rule)),
    );
  };

  const removeRule = (index) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const addPoint = (ruleIndex) => {
    setRules((prev) =>
      prev.map((rule, i) =>
        i === ruleIndex
          ? { ...rule, points: [...(rule.points || []), createEmptyPoint()] }
          : rule,
      ),
    );
  };

  const updateTreeAtPath = (points, path, updater) => {
    if (!Array.isArray(points) || path.length === 0) {
      return points;
    }

    const [head, ...rest] = path;
    return points.map((point, idx) => {
      if (idx !== head) return point;
      const normalized = normalizePoint(point);
      if (rest.length === 0) {
        return updater(normalized);
      }
      return {
        ...normalized,
        children: updateTreeAtPath(normalized.children || [], rest, updater),
      };
    });
  };

  const updatePointTextAtPath = (ruleIndex, path, value) => {
    setRules((prev) =>
      prev.map((rule, i) => {
        if (i !== ruleIndex) return rule;
        return {
          ...rule,
          points: updateTreeAtPath(rule.points || [], path, (node) => ({
            ...node,
            text: value,
          })),
        };
      }),
    );
  };

  const addChildPointAtPath = (ruleIndex, path) => {
    setRules((prev) =>
      prev.map((rule, i) => {
        if (i !== ruleIndex) return rule;
        return {
          ...rule,
          points: updateTreeAtPath(rule.points || [], path, (node) => ({
            ...node,
            children: [...(node.children || []), createEmptyPoint()],
          })),
        };
      }),
    );
  };

  const removePointAtPath = (ruleIndex, path) => {
    setRules((prev) =>
      prev.map((rule, i) => {
        if (i !== ruleIndex) return rule;

        const removeFromLevel = (points, remainingPath) => {
          const [head, ...rest] = remainingPath;
          if (rest.length === 0) {
            return points.filter((_, idx) => idx !== head);
          }

          return points.map((point, idx) => {
            if (idx !== head) return point;
            const normalized = normalizePoint(point);
            return {
              ...normalized,
              children: removeFromLevel(normalized.children || [], rest),
            };
          });
        };

        const nextPoints = removeFromLevel(rule.points || [], path);
        return {
          ...rule,
          points: nextPoints.length ? nextPoints : [createEmptyPoint()],
        };
      }),
    );
  };

  const cleanedRules = useMemo(() => {
    return (rules || [])
      .map((rule) => ({
        title: String(rule.title || "").trim(),
        points: (rule.points || []).map(sanitizePoint).filter(Boolean),
      }))
      .filter((rule) => rule.title || rule.points.length > 0);
  }, [rules]);

  const handleSave = async () => {
    setNotice("");
    setError("");

    if (cleanedRules.length === 0) {
      setError("Please add at least one rule");
      return;
    }

    const hasInvalid = cleanedRules.some(
      (rule) =>
        !rule.title ||
        rule.points.length === 0 ||
        !rule.points.some(hasAnyPointText),
    );
    if (hasInvalid) {
      setError("Each rule needs a title and at least one bullet point");
      return;
    }

    setSaving(true);
    try {
      const response = await devRulesService.updateRules(cleanedRules);
      const data = response?.data || {};
      setRules(Array.isArray(data.rules) ? data.rules.map(normalizeRule) : []);
      setMeta({
        version: data.version || 0,
        updatedAt: data.updatedAt || null,
        updatedBy: data.updatedBy || null,
      });
      setNotice(
        "Rules updated successfully. All users are now required to review them.",
      );
      await refreshLockState();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update rules");
    } finally {
      setSaving(false);
    }
  };

  const updatedByName =
    meta?.updatedBy?.fullNameEn || meta?.updatedBy?.nickname || "Unknown";

  const renderViewerPoint = (point, pathKey = "") => {
    const node = normalizePoint(point);
    return (
      <li key={pathKey || "root"}>
        <span>{node.text || ""}</span>
        {Array.isArray(node.children) && node.children.length > 0 && (
          <ul className="mt-2 ml-5 list-disc space-y-1 text-gray-700 dark:text-light-300">
            {node.children.map((child, childIdx) =>
              renderViewerPoint(child, `${pathKey}-${childIdx}`),
            )}
          </ul>
        )}
      </li>
    );
  };

  const renderEditorPoint = (point, ruleIndex, path = [], depth = 0) => {
    const node = normalizePoint(point);
    const pathKey = path.join("-") || "root";
    return (
      <li key={pathKey} className="flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <span
            className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${depth === 0 ? "bg-primary-accent" : "bg-gray-400 dark:bg-light-500"}`}
          />
          <div className="flex-1 flex items-center gap-2">
            <input
              value={node.text || ""}
              onChange={(e) =>
                updatePointTextAtPath(ruleIndex, path, e.target.value)
              }
              className="w-full bg-transparent border border-gray-300 dark:border-dark-200 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-light-100 focus:outline-none focus:ring-2 focus:ring-primary-accent/30"
              placeholder={depth === 0 ? "Bullet point" : "Sub bullet"}
            />
            <button
              onClick={() => addChildPointAtPath(ruleIndex, path)}
              className="px-2 py-1 rounded-md text-xs text-primary-accent border border-primary-accent/40 hover:bg-primary-accent/10"
              title="Add sub-bullet"
            >
              + Sub
            </button>
            <button
              onClick={() => removePointAtPath(ruleIndex, path)}
              className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Remove bullet"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {Array.isArray(node.children) && node.children.length > 0 && (
          <ul className="ml-5 space-y-2">
            {node.children.map((child, childIdx) =>
              renderEditorPoint(
                child,
                ruleIndex,
                [...path, childIdx],
                depth + 1,
              ),
            )}
          </ul>
        )}
      </li>
    );
  };

  return (
    <PageWrapper
      title="Dev Team Rules"
      subtitle="A single source of truth for how we work as a team"
      actions={
        canManage ? (
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            icon={
              saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )
            }
          >
            {saving ? "Saving..." : "Save Rules"}
          </Button>
        ) : null
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300">
          <ShieldCheck className="w-4 h-4" />
          Version {meta.version}
        </span>
        <span className="text-gray-500 dark:text-light-500">
          Last update: {formatDateTime(meta.updatedAt)}
        </span>
        <span className="text-gray-500 dark:text-light-500">
          Updated by: {updatedByName}
        </span>
      </div>

      {locked && (
        <div className="mb-6 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-amber-800 dark:text-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            You are reviewing the latest rules update. Access is restored after
            this page loads.
          </span>
        </div>
      )}

      {notice && (
        <div className="mb-6 rounded-xl border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-green-700 dark:text-green-300">
          {notice}
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-xl border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="card flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-primary-accent" />
        </div>
      ) : (
        <div className="space-y-4">
          {(rules || []).map((rule, ruleIndex) => (
            <motion.div
              key={ruleIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="card"
            >
              <div className="flex items-start justify-between gap-2 mb-4">
                {canManage ? (
                  <input
                    value={rule.title || ""}
                    onChange={(e) => updateRuleTitle(ruleIndex, e.target.value)}
                    className="w-full bg-transparent border border-gray-300 dark:border-dark-200 rounded-lg px-3 py-2 text-gray-900 dark:text-light-100 focus:outline-none focus:ring-2 focus:ring-primary-accent/30"
                    placeholder="Rule title"
                  />
                ) : (
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-light-100">
                    {rule.title}
                  </h3>
                )}

                {canManage && (
                  <button
                    onClick={() => removeRule(ruleIndex)}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Remove rule"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <ul className="space-y-2">
                {(rule.points || []).map((point, pointIndex) =>
                  canManage
                    ? renderEditorPoint(point, ruleIndex, [pointIndex], 0)
                    : renderViewerPoint(point, `${ruleIndex}-${pointIndex}`),
                )}
              </ul>

              {canManage && (
                <button
                  onClick={() => addPoint(ruleIndex)}
                  className="mt-3 text-sm text-primary-accent hover:text-orange-600"
                >
                  + Add bullet point
                </button>
              )}
            </motion.div>
          ))}

          {canManage && (
            <button
              onClick={addRule}
              className="w-full card border-2 border-dashed border-primary-accent/40 text-primary-accent hover:bg-primary-accent/5 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Rule Section
            </button>
          )}

          {!canManage && rules.length === 0 && (
            <div className="card text-center text-gray-500 dark:text-light-500 py-12">
              No rules have been published yet.
            </div>
          )}
        </div>
      )}
    </PageWrapper>
  );
};

export default DevRules;
